import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Mic, ChevronDown, ChevronUp, MessageSquare, Volume2, Loader2, ArrowRight, Star,
} from "lucide-react";
import { fetchPeerReviewVoiceBlobUrl } from "@/lib/apiClient";
import { useI18n } from "@/contexts/I18nContext";

// Feedback other reviewers have left on the current user's own submissions —
// moved here from Dashboard.jsx so it can be reused inside the Certificate
// hub's Peer Assessment tab as well.
const RUBRIC_LABEL_KEYS = { fluency: "feedback_received.fluency", accuracy: "feedback_received.accuracy", confidence: "feedback_received.confidence", continuity: "feedback_received.continuity" };

function StarRow({ score }) {
  return (
    <span className="inline-flex gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star key={s} className={`w-3.5 h-3.5 ${s <= Math.round(score) ? "text-[#D97706] fill-[#D97706]" : "text-[#E7E0D2]"}`} />
      ))}
    </span>
  );
}

function VoicePlayer({ voiceFeedbackId }) {
  const { t } = useI18n();
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (url) return;
    setLoading(true);
    try {
      const blobUrl = await fetchPeerReviewVoiceBlobUrl(voiceFeedbackId);
      setUrl(blobUrl);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  };

  if (url) return <audio src={url} controls className="w-full h-8 mt-2" />;
  return (
    <button onClick={load} disabled={loading}
      className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#064E3B] font-medium hover:underline disabled:opacity-50">
      {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Volume2 className="w-3.5 h-3.5" />}
      {loading ? t("feedback_received.loading") : t("feedback_received.play_voice_note")}
    </button>
  );
}

function SubmissionCard({ sub }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const decisions = sub.decisions || [];
  const avg = sub.rubric_avg;
  const comments = decisions.filter((d) => d.rubric?.comment?.trim());
  const voiceDecisions = decisions.filter((d) => d.voice_feedback_id);
  const flaggedDecisions = decisions.filter((d) => d.flagged_words?.length);

  return (
    <div className="rounded-2xl bg-white border border-[#E7E0D2] overflow-hidden">
      <button className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-[#FDFBF7] transition"
        onClick={() => setOpen((v) => !v)}>
        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-medium text-[#1F2937] text-sm">{sub.module_name || sub.module_code || t("feedback_received.recitation")}</span>
          <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${
            sub.status === "open" ? "bg-[#FFF7ED] text-[#D97706]" : "bg-[#F3F4F6] text-[#6B7280]"
          }`}>{sub.status}</span>
          <span className="text-xs text-[#4B5563]">
            {t("feedback_received.reviews_received", { count: decisions.length })}
          </span>
          {sub.peer_reviewed_badge && (
            <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#ECFDF5] text-[#064E3B]">
              ✓ {t("feedback_received.peer_reviewed")}
            </span>
          )}
        </div>
        <span className="ml-3 shrink-0 text-[#4B5563]">
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </span>
      </button>

      {open && (
        <div className="px-5 pb-5 border-t border-[#E7E0D2]">
          {decisions.length === 0 ? (
            <p className="mt-4 text-sm text-[#4B5563] italic">{t("feedback_received.awaiting_reviews")}</p>
          ) : (
            <>
              {avg && (
                <div className="mt-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-[#4B5563] mb-3">{t("feedback_received.average_scores")}</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {Object.entries(RUBRIC_LABEL_KEYS).map(([key, labelKey]) => (
                      <div key={key} className="rounded-xl bg-[#FDFBF7] border border-[#E7E0D2] p-3 text-center">
                        <div className="text-xs text-[#4B5563] mb-1">{t(labelKey)}</div>
                        <div className="text-lg font-semibold text-[#1F2937]">{(avg[key] ?? 0).toFixed(1)}</div>
                        <StarRow score={avg[key] ?? 0} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {comments.length > 0 && (
                <div className="mt-5">
                  <p className="text-xs font-semibold uppercase tracking-wider text-[#4B5563] mb-2 flex items-center gap-1.5">
                    <MessageSquare className="w-3.5 h-3.5" /> {t("feedback_received.reviewer_comments")}
                  </p>
                  <ul className="space-y-2">
                    {comments.map((d) => (
                      <li key={d.decision_id} className="text-sm text-[#1F2937] bg-[#FDFBF7] border-l-2 border-[#064E3B] pl-3 py-2 rounded-r-lg italic">
                        "{d.rubric.comment}"
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {flaggedDecisions.length > 0 && (
                <div className="mt-5">
                  <p className="text-xs font-semibold uppercase tracking-wider text-[#4B5563] mb-2">{t("feedback_received.words_to_work_on")}</p>
                  <ul className="space-y-2">
                    {flaggedDecisions.map((d) => (
                      <li key={d.decision_id} className="flex flex-wrap gap-1.5">
                        {d.flagged_words.map((w, i) => (
                          <span key={i} className="font-quran text-sm px-2 py-1 rounded bg-[#FEF2F2] text-[#EF4444] border border-[#FECACA]">{w}</span>
                        ))}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {voiceDecisions.length > 0 && (
                <div className="mt-5">
                  <p className="text-xs font-semibold uppercase tracking-wider text-[#4B5563] mb-1 flex items-center gap-1.5">
                    <Volume2 className="w-3.5 h-3.5" /> {t("feedback_received.voice_feedback")}
                  </p>
                  {voiceDecisions.map((d) => (
                    <VoicePlayer key={d.decision_id} voiceFeedbackId={d.voice_feedback_id} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function FeedbackReceivedSection({ submissions }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const totalReviews = submissions.reduce((acc, s) => acc + (s.decisions?.length || 0), 0);

  return (
    <div className="mt-6" data-testid="feedback-received-section">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left rounded-2xl bg-white border border-[#E7E0D2] p-5 hover:border-[#064E3B] transition group"
      >
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-[0.18em] text-[#064E3B] font-semibold">{t("feedback_received.title")}</div>
          <div className="flex items-center gap-2">
            <Mic className="w-5 h-5 text-[#064E3B]" />
            <ChevronDown className={`w-4 h-4 text-[#4B5563] transition-transform ${open ? "rotate-180" : ""}`} />
          </div>
        </div>
        <div className="mt-3 flex items-baseline gap-1">
          <div className="font-heading text-3xl font-semibold text-[#1F2937] tabular-nums">{totalReviews}</div>
          <div className="text-xs text-[#4B5563]">{t("feedback_received.review_label", { count: totalReviews })}</div>
        </div>
        <div className="text-xs text-[#4B5563] mt-1">
          {submissions.length === 0
            ? t("feedback_received.no_submissions_yet")
            : t("feedback_received.across_submissions", { count: submissions.length })}
        </div>
        <div className="mt-2 text-xs text-[#064E3B] group-hover:underline">
          {open ? t("feedback_received.hide_details") : t("feedback_received.view_feedback")}
        </div>
      </button>

      {open && (
        <div className="mt-3 flex flex-col gap-3">
          {submissions.length === 0 ? (
            <div className="rounded-2xl bg-white border border-[#E7E0D2] px-6 py-8 text-center">
              <Mic className="w-8 h-8 text-[#E7E0D2] mx-auto mb-3" />
              <p className="text-sm text-[#4B5563]">{t("feedback_received.no_submissions_body")}</p>
              <Link to="/certificate?tab=peer" className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-[#064E3B] hover:underline">
                {t("feedback_received.go_to_peer_assessment")} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          ) : (
            submissions.map((sub) => <SubmissionCard key={sub.submission_id} sub={sub} />)
          )}
        </div>
      )}
    </div>
  );
}
